/**
 * Biometric (Face ID / Touch ID / Android BiometricPrompt) wrapper.
 *
 * Model:
 *   - Auth source of truth = HttpOnly refresh-token cookie (server-issued).
 *     JavaScript can't read it; the browser sends it automatically on
 *     /api/auth/refresh.
 *   - Biometric is a launch-time gate, not a credential store. When the user
 *     enables biometric, we drop a sentinel value behind biometric-protected
 *     Keychain access. On next launch the unlock screen verifies biometric,
 *     fetches a fresh access token via /api/auth/refresh, and lets the app
 *     render. Without biometric the sentinel is unreachable and the gate
 *     stays closed even if someone has the JWT in localStorage.
 *   - "Enabled" flag lives in localStorage as a UX hint. It controls whether
 *     the unlock route is shown — the real gate is whether the biometric
 *     prompt succeeds.
 */

import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';

// The keychain "server" name is a logical group within the app's own
// keychain sandbox. iOS scopes keychain access by bundle ID at the OS
// level, so two different apps can't see each other's items even if they
// use the same server string — but using each app's bundle as the server
// name keeps the keychain inspector readable and avoids accidental cross-
// app expectations. Configured per app via `configureBiometric()`. Default
// is intentionally non-app-specific; each app should call configure() at
// startup with its own bundle ID.
let keychainServer = 'com.packetprospector.app';
const ENABLED_KEY = 'biometricAuthEnabled';
// Sentinel value stored behind biometric. The contents don't matter — what
// matters is that getCredentials only succeeds after biometric prompt.
const SENTINEL_USERNAME = 'biometric-session';
const SENTINEL_PASSWORD = 'enabled';

/**
 * Configure per-app keychain scoping. Call once during app startup before
 * any biometric helper is used. Required so the customer-portal app and
 * Dispatch each get their own keychain item label even though iOS already
 * sandboxes them at the bundle ID level.
 */
export function configureBiometric(options: { keychainServer: string }): void {
  keychainServer = options.keychainServer;
}

/**
 * The configured keychain "server" scope for this app (see
 * {@link configureBiometric}). Exposed so other native-secure-storage
 * helpers (refresh-token storage, the `storage.ts` secure-key mirror) can
 * derive their own Keychain/Keystore-backed credential slots that are
 * scoped per-app but distinct from the biometric sentinel above — each
 * caller namespaces this value with its own suffix so the credential slots
 * never collide.
 */
export function getKeychainServer(): string {
  return keychainServer;
}

export type BiometryLabel = 'Face ID' | 'Touch ID' | 'Biometrics';

/**
 * True iff running in the Capacitor native shell AND the device has
 * biometric hardware available + enrolled.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const result = await NativeBiometric.isAvailable();
    return result.isAvailable;
  } catch {
    return false;
  }
}

/**
 * Human-readable name for the available biometric, for use in button copy
 * and toggle labels. Falls back to "Biometrics" when we can't tell.
 */
export async function getBiometryLabel(): Promise<BiometryLabel> {
  if (!Capacitor.isNativePlatform()) return 'Biometrics';
  try {
    const result = await NativeBiometric.isAvailable();
    if (!result.isAvailable) return 'Biometrics';
    if (
      result.biometryType === BiometryType.FACE_ID ||
      result.biometryType === BiometryType.FACE_AUTHENTICATION
    ) {
      return 'Face ID';
    }
    if (
      result.biometryType === BiometryType.TOUCH_ID ||
      result.biometryType === BiometryType.FINGERPRINT
    ) {
      return 'Touch ID';
    }
    return 'Biometrics';
  } catch {
    return 'Biometrics';
  }
}

export function isBiometricEnabled(): boolean {
  return localStorage.getItem(ENABLED_KEY) === 'true';
}

// Tracks an explicit "Not now" on the post-login enrollment offer so the
// user isn't re-asked on every sign-in. Deliberately NOT set by
// disableBiometric(): the unlock screen's invalidated-Keychain recovery
// path clears enrollment specifically so the post-login offer re-appears
// and re-enrolls a fresh Keychain entry.
const ENROLL_DECLINED_KEY = 'biometricEnrollDeclined';

export function markEnrollmentDeclined(): void {
  localStorage.setItem(ENROLL_DECLINED_KEY, 'true');
}

export function wasEnrollmentDeclined(): boolean {
  return localStorage.getItem(ENROLL_DECLINED_KEY) === 'true';
}

/**
 * Enroll: prompt biometric to confirm intent, store the sentinel, set flag.
 * Throws if the user cancels or the prompt fails.
 */
export async function enableBiometric(): Promise<void> {
  const label = await getBiometryLabel();
  await NativeBiometric.verifyIdentity({
    reason: `Enable ${label} for Dispatch`,
    title: `Enable ${label}`,
    subtitle: 'Confirm your identity to enable biometric sign-in.',
  });
  await NativeBiometric.setCredentials({
    username: SENTINEL_USERNAME,
    password: SENTINEL_PASSWORD,
    server: keychainServer,
  });
  localStorage.setItem(ENABLED_KEY, 'true');
  // A completed enrollment supersedes any past "Not now"
  localStorage.removeItem(ENROLL_DECLINED_KEY);
}

/**
 * Disable: clear the Keychain sentinel and the flag. Safe to call when not
 * enrolled — deleteCredentials swallows "no entry" errors.
 */
export async function disableBiometric(): Promise<void> {
  try {
    await NativeBiometric.deleteCredentials({ server: keychainServer });
  } catch {
    // No credentials to delete is fine.
  }
  localStorage.removeItem(ENABLED_KEY);
}

/**
 * Verify the user with biometric and retrieve the Keychain sentinel.
 * Returns true on success; throws on cancel / failure (caller surfaces an
 * appropriate fallback like "use password instead").
 */
export async function verifyBiometric(): Promise<boolean> {
  const label = await getBiometryLabel();
  await NativeBiometric.verifyIdentity({
    reason: `Sign in to Dispatch with ${label}`,
    title: 'Sign in',
    subtitle: `Use ${label} to unlock Dispatch.`,
  });
  // verifyIdentity itself proves biometric; the getCredentials call also
  // verifies the Keychain entry still exists (it can be invalidated if the
  // user changes their biometric enrollment on the device).
  const creds = await NativeBiometric.getCredentials({ server: keychainServer });
  return (
    creds.username === SENTINEL_USERNAME && creds.password === SENTINEL_PASSWORD
  );
}
